<template>
  <div :class="['ecs', `ecs--${variant}`]">
    <!-- Sad book character -->
    <div class="ecs__character">
      <svg
        class="ecs__book-svg"
        width="140"
        height="160"
        viewBox="0 0 140 160"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <!-- Shadow -->
        <ellipse
          class="ecs__shadow"
          cx="70"
          cy="148"
          rx="36"
          ry="6"
          fill="currentColor"
          opacity="0.07"
        />

        <!-- Left leg -->
        <path
          class="ecs__leg ecs__leg--left"
          d="M52 126 C52 134, 48 140, 44 142"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
        />
        <!-- Left foot -->
        <ellipse
          cx="42"
          cy="143"
          rx="5"
          ry="3"
          fill="currentColor"
          opacity="0.15"
        />

        <!-- Right leg -->
        <path
          class="ecs__leg ecs__leg--right"
          d="M82 126 C82 134, 86 140, 90 142"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
        />
        <!-- Right foot -->
        <ellipse
          cx="92"
          cy="143"
          rx="5"
          ry="3"
          fill="currentColor"
          opacity="0.15"
        />

        <!-- Book body (main cover) -->
        <rect
          class="ecs__body"
          x="32"
          y="32"
          width="72"
          height="96"
          rx="6"
          fill="var(--ecs-cover)"
        />
        <!-- Book spine -->
        <rect
          x="32"
          y="32"
          width="10"
          height="96"
          rx="3"
          fill="var(--ecs-spine)"
        />
        <!-- Book spine highlight -->
        <rect
          x="34"
          y="36"
          width="3"
          height="88"
          rx="1.5"
          fill="var(--ecs-cover)"
          opacity="0.5"
        />
        <!-- Book page edges (right side) -->
        <rect
          x="100"
          y="38"
          width="6"
          height="84"
          rx="2"
          fill="var(--ecs-page)"
        />
        <line
          x1="102"
          y1="44"
          x2="102"
          y2="116"
          stroke="var(--ecs-page-line)"
          stroke-width="0.5"
        />
        <line
          x1="104"
          y1="44"
          x2="104"
          y2="116"
          stroke="var(--ecs-page-line)"
          stroke-width="0.5"
        />

        <!-- Face area (lighter patch) -->
        <rect
          x="46"
          y="52"
          width="50"
          height="44"
          rx="4"
          fill="var(--ecs-face)"
          opacity="0.5"
        />

        <!-- Left eye -->
        <circle
          cx="58"
          cy="68"
          r="5"
          fill="white"
        />
        <circle
          class="ecs__pupil ecs__pupil--left"
          cx="59"
          cy="69"
          r="2.5"
          fill="currentColor"
        />
        <!-- Right eye -->
        <circle
          cx="80"
          cy="68"
          r="5"
          fill="white"
        />
        <circle
          class="ecs__pupil ecs__pupil--right"
          cx="81"
          cy="69"
          r="2.5"
          fill="currentColor"
        />

        <!-- Sad mouth -->
        <path
          class="ecs__mouth"
          d="M63 86 Q69 82, 75 86"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          fill="none"
        />

        <!-- Teardrop -->
        <g class="ecs__tear">
          <path
            d="M90 60 Q92 54, 94 60 Q94 63, 92 64 Q90 63, 90 60Z"
            fill="var(--ecs-tear)"
            opacity="0.8"
          />
        </g>

        <!-- Left arm -->
        <path
          class="ecs__arm ecs__arm--left"
          d="M36 90 C28 96, 26 104, 30 108"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
        />

        <!-- Right arm -->
        <path
          class="ecs__arm ecs__arm--right"
          d="M100 90 C108 96, 110 104, 106 108"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          fill="none"
        />
      </svg>
    </div>

    <!-- Copy -->
    <h2 class="ecs__title">
      {{ $t('shop.emptyCartTitle') }}
    </h2>
    <p class="ecs__subtitle">
      {{ $t('shop.emptyCartSubtitle') }}
    </p>

    <!-- CTA -->
    <button
      class="ecs__cta"
      @click="browse"
    >
      {{ $t('shop.startBrowsing') }}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line
          x1="5"
          y1="12"
          x2="19"
          y2="12"
        />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'EmptyCartState',
  props: {
    variant: {
      type: String,
      default: 'page',
      validator: (value) => ['page', 'drawer'].includes(value),
    },
  },
  methods: {
    browse() {
      this.$emit('browse');
      if (this.$route.name !== 'home') {
        this.$router.push({ name: 'home' });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ecs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  min-height: 60vh;
  color: $book-ink;
}

.ecs--drawer {
  justify-content: flex-start;
  padding: 36px 20px 28px;
  min-height: auto;
}

/* ── Book character ── */
.ecs__character {
  --ecs-cover: #F5A623;
  --ecs-spine: #E8941E;
  --ecs-face: #FFC94D;
  --ecs-page: #f0ebe3;
  --ecs-page-line: #e0d9cf;
  --ecs-tear: #6CB4EE;
  margin-bottom: 24px;
}

.ecs--drawer .ecs__character {
  margin-bottom: 18px;
}

.ecs__book-svg {
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.06));
}

.ecs--drawer .ecs__book-svg {
  width: 112px;
  height: 128px;
}

/* Gentle idle bob */
.ecs__body {
  animation: ecs-bob 3s ease-in-out infinite;
  transform-origin: center bottom;
}

@keyframes ecs-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

/* Teardrop falls and fades */
.ecs__tear {
  animation: ecs-tear 2.8s ease-in infinite;
}

@keyframes ecs-tear {
  0% { opacity: 0; transform: translateY(0); }
  10% { opacity: 0.8; }
  80% { opacity: 0.3; }
  100% { opacity: 0; transform: translateY(18px); }
}

/* Pupil micro-drift */
.ecs__pupil--left,
.ecs__pupil--right {
  animation: ecs-look 5s ease-in-out infinite;
}

@keyframes ecs-look {
  0%, 100% { transform: translate(0, 0); }
  30% { transform: translate(-1px, 0.5px); }
  60% { transform: translate(1px, -0.5px); }
}

/* Legs swing */
.ecs__leg--left {
  animation: ecs-swing-l 2.5s ease-in-out infinite;
  transform-origin: 52px 126px;
}

.ecs__leg--right {
  animation: ecs-swing-r 2.5s ease-in-out 0.4s infinite;
  transform-origin: 82px 126px;
}

@keyframes ecs-swing-l {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(4deg); }
}

@keyframes ecs-swing-r {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-4deg); }
}

/* Shadow pulses with bob */
.ecs__shadow {
  animation: ecs-shadow 3s ease-in-out infinite;
}

@keyframes ecs-shadow {
  0%, 100% { rx: 36; opacity: 0.07; }
  50% { rx: 32; opacity: 0.04; }
}

/* ── Typography ── */
.ecs__title {
  font-family: $book-font-display;
  font-size: 1.35rem;
  font-weight: 800;
  color: $book-ink;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}

.ecs--drawer .ecs__title {
  font-size: 1.1rem;
  margin-bottom: 6px;
}

.ecs__subtitle {
  font-family: $book-font-body;
  font-size: 0.9rem;
  font-weight: 400;
  color: $book-grey;
  margin: 0 0 28px;
  max-width: 280px;
  line-height: 1.5;
}

.ecs--drawer .ecs__subtitle {
  font-size: 0.8125rem;
  max-width: 240px;
  margin-bottom: 22px;
}

/* ── CTA button ── */
.ecs__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-image: $book-brand-gradient;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: box-shadow 0.3s $book-ease-out, transform 0.3s $book-ease-out;

  &:hover {
    box-shadow: $book-brand-shadow;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }
}

.ecs--drawer .ecs__cta {
  width: 100%;
  max-width: 280px;
  justify-content: center;
  padding: 13px 20px;
}

@media (max-width: 599px) {
  .ecs {
    padding: 40px 20px;
  }

  .ecs__character {
    margin-bottom: 20px;
  }

  .ecs__book-svg {
    width: 124px;
    height: 142px;
  }

  .ecs__title {
    font-size: 1.2rem;
  }

  .ecs__subtitle {
    font-size: 0.85rem;
    margin-bottom: 24px;
  }

  .ecs__cta {
    width: 100%;
    max-width: 320px;
    justify-content: center;
  }

  .ecs--drawer {
    padding: 28px 18px 24px;
  }
}

</style>
